// @ts-nocheck

import type {SurveyResponse} from 'src/api-parsers/events';
import type {ModuleSequenceNode} from 'src/stores/survey';
import type {Question, SurveyError} from 'src/types/survey';

import * as React from 'react';
import {Helmet} from 'react-helmet';
import partial from 'lodash/partial';
import range from 'lodash/range';
import findIndex from 'lodash/findIndex';

import {useWritebackAllowedAttributesKeyed} from 'src/hooks/useWritebackAllowedAttributes';

import SurveyQuestionComponent from './modules/survey-question';
import css from './survey.css';


const getResponse = (currentQuestion: unknown, currentResponse: unknown) => {
  const questionId = currentQuestion.id;
  const responseIndex = findIndex(
    currentResponse.responseModules,
    (response) => response.moduleId === questionId,
  );
  return currentResponse.responseModules[responseIndex];
};

const DumbSurvey = ({
  sentEventId,
  currentQuestion,
  currentResponse,
  currentError,
  progress,
  moduleSequence,
  handleResponse,
  handlePrev,
  handleNext,
  handleJump,
  brandColor,
  buttonColor,
  nextRequiredQuestionIndex = moduleSequence.length,
  agencyName,
  multiEntityEnabled,
  entityType,
  surveyAnswered,
}: DumbSurveyProps) => {
  const allowedAttributes = useWritebackAllowedAttributesKeyed(entityType);
  const writebackField = currentQuestion?.writeback?.attribute_name ?? '';
  const writebackFieldAttributes =
    allowedAttributes?.attribute_meta_data[writebackField];

  return (
    <div className={css.survey}>
      {agencyName && <Helmet titleTemplate="" title={agencyName} />}
      <div className={css.componentBox}>
        <SurveyQuestionComponent
          sentEventId={sentEventId}
          currentResponse={currentResponse}
          question={currentQuestion}
          response={
            currentResponse && getResponse(currentQuestion, currentResponse)
          }
          currentError={currentError}
          key={progress}
          handleNext={handleResponse}
          brandColor={brandColor}
          buttonColor={buttonColor}
          isLast={moduleSequence.length - 1 === progress}
          multiEntityEnabled={multiEntityEnabled}
          writebackFieldAttributes={writebackFieldAttributes}
        />
      </div>

      <PrevNext
        total={moduleSequence.length}
        current={progress}
        prev={handlePrev}
        next={handleNext}
        brandColor={brandColor}
        nextRequiredQuestionIndex={nextRequiredQuestionIndex}
        surveyAnswered={surveyAnswered}
      >
        <ProgressIndicator
          total={moduleSequence.length}
          current={progress}
          jump={handleJump}
          nextRequiredQuestionIndex={nextRequiredQuestionIndex}
        />
      </PrevNext>
    </div>
  );
};

type DumbSurveyProps = {
  sentEventId: string;
  currentResponse?: SurveyResponse;
  progress: number;
  handleResponse: any;
  handleJump: any;
  handleNext: any;
  handlePrev: any;
  brandColor: string;
  buttonColor: string;
  moduleSequence: ModuleSequenceNode[];
  currentQuestion: Question;
  currentError: SurveyError;
  nextRequiredQuestionIndex?: number;
  agencyName?: string;
  multiEntityEnabled: boolean;
  surveyAnswered: boolean;
};

export default DumbSurvey;

type ProgressProps = {
  total: number;
  current: number;
  jump: (index: number) => void;
  nextRequiredQuestionIndex: number;
};
const ProgressIndicator = ({
  total,
  current,
  jump,
  nextRequiredQuestionIndex,
}: ProgressProps) => {
  let shouldBlockSection;
  return (
    <div className={css.progressContainer}>
      {range(total).map((_e, index) => {
        const isCurrent = index <= current;
        shouldBlockSection = index > nextRequiredQuestionIndex;
        return (
          <span
            key={index}
            onClick={!shouldBlockSection ? partial(jump, index) : null}
            className={
              isCurrent
                ? css.activeSection
                : shouldBlockSection
                ? css.blockedSection
                : css.section
            }
          />
        );
      })}
    </div>
  );
};

type PrevNextProps = {
  children?: React.ReactElement<any>;
  total: number;
  current: number;
  prev: () => void;
  next: () => void;
  brandColor: string | null | undefined;
  nextRequiredQuestionIndex: number;
  surveyAnswered: boolean;
};

const PrevNext = ({
  children,
  total,
  current,
  prev,
  next,
  brandColor,
  nextRequiredQuestionIndex,
  surveyAnswered,
}: PrevNextProps) => (
  <div
    className={css.prevNext}
    style={brandColor && {backgroundColor: brandColor}}
  >
    {current !== 0 ? (
      <button className={css.prevButton} onClick={prev}>
        &larr;
      </button>
    ) : (
      <span className={css.spacer}>&nbsp;</span>
    )}
    {children}
    {current !== total - 1 &&
    current < nextRequiredQuestionIndex &&
    (current !== 0 || surveyAnswered) ? (
      <button className={css.nextButton} onClick={next}>
        &rarr;
      </button>
    ) : (
      <span className={css.spacer}>&nbsp;</span>
    )}
  </div>
);
